<template>
  <div id="gatewayConfigurationHistory">
    <rollback :eui="eui" />

    <grid id="gatewayConfigurationHistoryGrid"
          :title="$t('gc-history:configurationHistory')"
          :columns="columns"
          :paginated-dto="history"
          :pagination="false"
          @load="getHistory"
    >
      <template slot="compare" slot-scope="props">
        <input :id="'history-' + props.dto.fromDate"
               v-model="selectedHistories"
               :value="props.dto"
               type="checkbox"
        >
        <label :for="'history-' + props.dto.fromDate" />
      </template>

      <template slot="fromDate" slot-scope="props">{{ props.dto.fromDate | date }}</template>
      <template slot="toDate" slot-scope="props">{{ props.dto.toDate | date }}</template>

      <a slot="details" slot-scope="props" :title="$t('gc-history:displayDetails') | capitalize" href @click.prevent="displayDetailsModal(props.dto)">
        {{ displayDetails(props.dto) | stringify | substring(100) }}
      </a>
    </grid>

    <button v-if="selectedHistories.length >= 2" id="compareButton" class="btn btn-primary mt-3" @click.prevent="displayComparisonModal">{{ $t('gc-history:compareSelectedConfiguration') }}</button>

    <modal id="detailsModal"
           v-model="showDetailsModal"
           :title="$t('gc-history:configurationDetails')"
           size="lg"
    >
      <table slot="modal-body" class="table table-sm table-borderless">
        <thead>
          <tr>
            <th />
            <th>
              {{ $t('gc-history:from') | capitalize }} {{ detailsDto.fromDate | date }}<br>
              {{ $t('gc-history:to') | capitalize }} {{ detailsDto.toDate | date }}
            </th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="(key, keyIndex) in comparisonKeys" :key="keyIndex">
            <td>{{ $t('gc-history:configurationKeys.' + key) | capitalize }}</td>
            <td>{{ detailsDto[key] }}</td>
          </tr>
        </tbody>
      </table>
    </modal>

    <modal id="comparisonModal"
           v-model="showComparisonModal"
           :title="$t('gc-history:selectedConfigurationDetails')"
           size="lg"
    >
      <table slot="modal-body" class="table table-sm table-borderless">
        <thead>
          <tr>
            <th />
            <th v-for="(selectedHistory, indexVersion) in selectedHistoriesSortedAsc" :key="indexVersion">
              {{ $t('gc-history:from') | capitalize }} {{ selectedHistory.fromDate | date }}<br>
              {{ $t('gc-history:to') | capitalize }} {{ selectedHistory.toDate | date }}
            </th>
          </tr>
        </thead>

        <tbody>
          <tr v-for="(key, keyIndex) in comparisonKeys" :key="keyIndex">
            <td>{{ $t('gc-history:configurationKeys.' + key) | capitalize }}</td>

            <td v-for="(selectedHistory, historyIndex) in selectedHistoriesSortedAsc"
                :key="historyIndex"
                :class="{'bg-danger': historyIndex > 0 && JSON.stringify(selectedHistory[key]) !== JSON.stringify(selectedHistoriesSortedAsc[historyIndex - 1][key])}"
            >
              {{ selectedHistory[key] }}
            </td>
          </tr>
        </tbody>
      </table>
    </modal>
  </div>
</template>

<script>
import { orderBy, isEmpty, difference, map, omit } from 'lodash-es'
import { mapGetters } from 'vuex'
import to from '@/modules/await-to-js'
import subMonths from 'date-fns/sub_months'
import getTime from 'date-fns/get_time'

import Rollback from './Rollback'

export default {
  titleKey: 'gatewayConfHistory',

  components: { Rollback },

  props: {
    eui: {
      type: String,
      default: ''
    }
  },

  data () {
    return {
      history: {},
      // Details modal
      showDetailsModal: false,
      detailsDto: {},
      // Comparison
      selectedHistories: [],
      showComparisonModal: false,
      comparisonKeys: []
    }
  },

  computed: {
    ...mapGetters(['gateway']),

    columns () {
      return [
        { text: this.$t('gc-history:compare'), slot: 'compare', class: 'text-center' },
        { text: this.$t('gc-history:fromDate'), slot: 'fromDate' },
        { text: this.$t('gc-history:toDate'), slot: 'toDate' },
        { text: this.$t('gc-history:details'), slot: 'details' }
      ]
    },

    selectedHistoriesSortedAsc () {
      return orderBy(this.selectedHistories, ['fromDate', 'toDate'], ['asc', 'asc'])
    }
  },

  watch: {
    eui () {
      // Reset data, and get data from new gateway
      this.history = {}
    },

    gateway () {
      // Do not refresh is we olready have the data
      if (!isEmpty(this.history)) return

      this.getHistory()
    }
  },

  created () {
    this.getHistory()
  },

  methods: {
    async getHistory () {
      if (isEmpty(this.gateway)) return

      const fields = [
        'fromDate', 'toDate',
        'periodResolution', 'ntp1', 'ntp2',
        'durationThreshold', 'powerThreshold',
        'rollBackTmo',
        'configVpn',
        'wanInterfaceTypes',
        'login', 'password', 'apn', 'pinCode',
        'dhcp', 'defaultIp', 'defaultNetmask', 'defaultGateway',
        'ssid', 'mode', 'passwordWlan'
      ]
      this.comparisonKeys = difference(fields, ['fromDate', 'toDate'])

      const params = { startDate: getTime(subMonths(new Date(), 1)), endDate: Date.now(), fields: fields.join(',') }
      let error, response
      [error, response] = await to(this.$api.getGatewayConfigurations(this.gateway, params))
      if (error || !response) return

      this.history = response.data
      this.history.list = orderBy(this.history.list, ['fromDate', 'toDate'], ['desc', 'desc'])

      // Sort wanInterfaceTypes by index, and flatten array to only display wanInterfaceType
      for (let i = 0; i < this.history.list.length; i++) {
        this.history.list[i].wanInterfaceTypes = orderBy(this.history.list[i].wanInterfaceTypes, ['index'])
        this.history.list[i].wanInterfaceTypes = map(this.history.list[i].wanInterfaceTypes, type => type.wanInterfaceType)
      }
    },

    displayDetails (configurationsDto) {
      return omit(configurationsDto, ['fromDate', 'toDate'])
    },

    displayDetailsModal (configurationsDto) {
      this.detailsDto = configurationsDto
      this.showDetailsModal = true
    },

    displayComparisonModal () {
      this.showComparisonModal = true
    }
  }
}
</script>
